import { AudioMixer } from './AudioMixer';

export type PresetSoundType = 'plump' | 'bounce' | 'stretch' | 'water';

interface ActiveVoice {
  oscillator: OscillatorNode;
  gainNode: GainNode;
  startTime: number;
  stopTimerId: ReturnType<typeof setTimeout>;
}

export interface PresetSoundSynthesizerOptions {
  mixer: AudioMixer;
}

/**
 * プリセット効果音プロシージャル合成コンポーネント (PresetSoundComponent)
 * Web Audio API オシレーターによるゼロ遅延リアルタイム音響合成、
 * 物理入力強度連動スケーリング、および最大4音ポリフォニー（ボイススティーリング）を提供する。
 */
export class PresetSoundSynthesizer {
  private mixer: AudioMixer;
  private activeVoices: ActiveVoice[] = [];
  private readonly maxPolyphony: number = 4;

  constructor(options: PresetSoundSynthesizerOptions) {
    this.mixer = options.mixer;
  }

  /**
   * プリセットSEを即時合成・再生
   * @param soundType サウンド種別 ('plump' | 'bounce' | 'stretch' | 'water')
   * @param volumeScale 音量スケール (0.0〜1.0, デフォルト 1.0)
   * @param intensity 物理入力強度 (0.0〜1.0, デフォルト 0.5)
   */
  public playPresetSE(
    soundType: PresetSoundType,
    volumeScale: number = 1.0,
    intensity: number = 0.5
  ): void {
    if (this.mixer.getEdition() === 'web_free') {
      return;
    }

    const audioContext = this.mixer.getAudioContext();
    const destinationNode = this.mixer.getSEGainNode();
    if (!audioContext || !destinationNode) return;

    // Autoplay Policy 復帰
    void this.mixer.ensureRunning();

    // 強度（intensity）を 0.0〜1.0 に正規化
    const normalizedIntensity = Math.max(0.0, Math.min(1.0, intensity));
    const normalizedScale = Math.max(0.0, Math.min(1.0, volumeScale));

    // ポリフォニー制限（最大4音）：上限に達している場合は最古ボイスをスチール
    if (this.activeVoices.length >= this.maxPolyphony) {
      this.stealOldestVoice();
    }

    try {
      this.synthesizeSound(audioContext, destinationNode, soundType, normalizedScale, normalizedIntensity);
    } catch (err) {
      console.warn('[PresetSoundSynthesizer] Failed to synthesize sound:', err);
    }
  }

  /**
   * 発音中のすべての効果音を即時停止
   */
  public stopAllSE(): void {
    for (const voice of this.activeVoices) {
      this.stopVoiceGracefully(voice, 0.01);
    }
    this.activeVoices = [];
  }

  /**
   * ボイススティーリング: 最古の発音ノードを 10ms でフェードアウト破棄
   */
  private stealOldestVoice(): void {
    const oldest = this.activeVoices.shift();
    if (oldest) {
      this.stopVoiceGracefully(oldest, 0.01);
    }
  }

  private stopVoiceGracefully(voice: ActiveVoice, fadeDurationSec: number = 0.01): void {
    clearTimeout(voice.stopTimerId);
    const audioContext = this.mixer.getAudioContext();
    const now = audioContext ? (audioContext.currentTime ?? 0) : 0;

    try {
      if (typeof voice.gainNode.gain.setValueAtTime === 'function' && typeof voice.gainNode.gain.linearRampToValueAtTime === 'function') {
        voice.gainNode.gain.setValueAtTime(voice.gainNode.gain.value, now);
        voice.gainNode.gain.linearRampToValueAtTime(0.0001, now + fadeDurationSec);
      } else {
        voice.gainNode.gain.value = 0;
      }
      setTimeout(() => {
        try {
          voice.oscillator.stop();
          voice.oscillator.disconnect();
          voice.gainNode.disconnect();
        } catch {
          // ignore
        }
      }, fadeDurationSec * 1000 + 5);
    } catch {
      try {
        voice.oscillator.stop();
        voice.oscillator.disconnect();
        voice.gainNode.disconnect();
      } catch {
        // ignore
      }
    }
  }

  private synthesizeSound(
    audioCtx: AudioContext,
    destination: GainNode,
    soundType: PresetSoundType,
    volumeScale: number,
    intensity: number
  ): void {
    const now = audioCtx.currentTime ?? 0;

    // 物理連動パラメトリックスケーリング
    // Gain = BaseGain * (0.4 + 0.6 * I)
    const intensityGainMultiplier = 0.4 + 0.6 * intensity;
    const peakGain = 0.8 * volumeScale * intensityGainMultiplier;

    // Pitch = BaseFreq * (0.90 + 0.20 * I)
    const pitchMultiplier = 0.90 + 0.20 * intensity;

    const osc = audioCtx.createOscillator();
    const voiceGain = audioCtx.createGain();

    voiceGain.connect(destination);
    osc.connect(voiceGain);

    let durationSec = 0.25;

    switch (soundType) {
      case 'plump': {
        // ぷるん♪ (カスタードプリンの弾力感)
        // 波形: サイン波、0ms: 340Hz -> 80ms: 580Hz -> 220ms: 420Hz
        osc.type = 'sine';
        durationSec = 0.25;

        const f0 = 340 * pitchMultiplier;
        const fPeak = 580 * pitchMultiplier;
        const fEnd = 420 * pitchMultiplier;

        osc.frequency.setValueAtTime(f0, now);
        osc.frequency.exponentialRampToValueAtTime(fPeak, now + 0.08);
        osc.frequency.exponentialRampToValueAtTime(fEnd, now + 0.22);

        // ゲイン: アタック 5ms、250msで指数減衰
        voiceGain.gain.setValueAtTime(0.0001, now);
        voiceGain.gain.linearRampToValueAtTime(peakGain, now + 0.005);
        voiceGain.gain.exponentialRampToValueAtTime(0.0001, now + durationSec);
        break;
      }

      case 'bounce': {
        // ぽよん (弾性反発)
        // 波形: サイン波、0ms: 480Hz -> 300ms: 220Hz
        osc.type = 'sine';
        durationSec = 0.30;

        const f0 = 480 * pitchMultiplier;
        const fEnd = 220 * pitchMultiplier;

        osc.frequency.setValueAtTime(f0, now);
        osc.frequency.exponentialRampToValueAtTime(fEnd, now + durationSec);

        // ゲイン: アタック 8ms、300msで減衰
        voiceGain.gain.setValueAtTime(0.0001, now);
        voiceGain.gain.linearRampToValueAtTime(peakGain, now + 0.008);
        voiceGain.gain.exponentialRampToValueAtTime(0.0001, now + durationSec);
        break;
      }

      case 'stretch': {
        // 伸び (スライム・餅の伸長)
        // 波形: 三角波、260Hz + intensity * 160Hz
        osc.type = 'triangle';
        durationSec = 0.20;

        const f0 = 260 * pitchMultiplier;
        const fEnd = (260 + intensity * 160) * pitchMultiplier;

        osc.frequency.setValueAtTime(f0, now);
        osc.frequency.linearRampToValueAtTime(fEnd, now + durationSec);

        // 控えめな音量
        const stretchGain = peakGain * 0.4;
        voiceGain.gain.setValueAtTime(0.0001, now);
        voiceGain.gain.linearRampToValueAtTime(stretchGain, now + 0.02);
        voiceGain.gain.exponentialRampToValueAtTime(0.0001, now + durationSec);
        break;
      }

      case 'water': {
        // 水滴 (清涼な高周波インパルス)
        // 波形: 三角波、800Hz -> 1200Hz (50ms)
        osc.type = 'triangle';
        durationSec = 0.15;

        const f0 = 800 * pitchMultiplier;
        const fEnd = 1200 * pitchMultiplier;

        osc.frequency.setValueAtTime(f0, now);
        osc.frequency.linearRampToValueAtTime(fEnd, now + 0.05);

        // アタック 2ms、150msで減衰
        voiceGain.gain.setValueAtTime(0.0001, now);
        voiceGain.gain.linearRampToValueAtTime(peakGain * 0.6, now + 0.002);
        voiceGain.gain.exponentialRampToValueAtTime(0.0001, now + durationSec);
        break;
      }
    }

    osc.start(now);
    osc.stop(now + durationSec + 0.02);

    const voiceRecord: ActiveVoice = {
      oscillator: osc,
      gainNode: voiceGain,
      startTime: now,
      stopTimerId: setTimeout(() => {
        try {
          osc.disconnect();
          voiceGain.disconnect();
        } catch {
          // ignore
        }
        const index = this.activeVoices.indexOf(voiceRecord);
        if (index !== -1) {
          this.activeVoices.splice(index, 1);
        }
      }, (durationSec + 0.05) * 1000),
    };

    this.activeVoices.push(voiceRecord);
  }

  public getActiveVoicesCount(): number {
    return this.activeVoices.length;
  }
}
